const PROD_DEFAULT_CERT =
  'arn:aws:acm:us-east-1:851725546043:certificate/94d1ad17-cd04-4202-945b-8de5e3fbb76c';
const NON_PROD_DEFAULT_CERT =
  'arn:aws:acm:us-east-1:256023342468:certificate/a8dcca9d-094c-4460-b56e-85302dca519c';
const NON_PROD_BASE_DOMAIN = 'ilotto.com.br';

export type WL = 'sbt' | 'h2b' | 'orp' | 'wep' | 'demo' | 'msp';
export enum EnvStage {
  HML = 'hml',
  PRD = 'prd',
}

/** Single Datadog RUM application for all whitelabels */
export const DATADOG_RUM_APPLICATION_ID = '96e71669-6726-4031-8b8c-426786756623';
/** Datadog site — single org; not an env override */
export const DATADOG_SITE = 'datadoghq.com';
export const DATADOG_SESSION_SAMPLE_RATE = 1;
export const DATADOG_SESSION_REPLAY_SAMPLE_RATE = 20;

type WLStaticConfig = {
  faviconSource: string;
  domain: string;
  cert?: string;
  /**
   * Hostnames extras servidos pela mesma distribuicao em producao, alem do
   * `domain`. Precisam estar cobertos pelo mesmo certificado.
   */
  aliases?: string[];
};

type WLStageConfig = {
  apiUrl: string;
  apiInternalUrl?: string;
  /**
   * Hostname do ALB da API. Quando definido, as chamadas server-side resolvem
   * o hostname público para este endereço e saem sem passar pelo Cloudflare.
   * A URL, o SNI e o Host continuam sendo os públicos. Ver utils/internalApiDns.
   */
  apiInternalAlbHost?: string;
  gtmId?: string;
  logLevel?: string;
  forwardLogs?: boolean;
  /** Exige passe Turnstile nas consultas de CPF. Default: false. */
  turnstileEnforce?: boolean;
};

const WL_static: Record<WL, WLStaticConfig> = {
  sbt: {
    faviconSource: 'favicon/favicon-32x32.png',
    domain: 'sbtpremios.com',
    cert: 'arn:aws:acm:us-east-1:851725546043:certificate/8c887e05-2506-426d-b892-497763eb5991',
  },
  h2b: {
    faviconSource: 'favicon/Fav-h2premio-32x32.png',
    domain: 'h2premios.com.br',
    cert: 'arn:aws:acm:us-east-1:851725546043:certificate/c0a60748-f933-44e7-ab15-60f33c5268ad',
  },
  orp: {
    faviconSource: 'favicon/Fav-ouropix.png',
    domain: 'ouropix.com.br',
  },
  wep: {
    faviconSource: 'favicon/Fav-wepink.png',
    domain: 'wep.ilotto.com.br',
  },
  demo: {
    faviconSource: 'favicon/Fav-ilotto.png',
    domain: 'demo.ilotto.com.br',
  },
  msp: {
    faviconSource: 'favicon/Fav-massapremios.png',
    domain: 'massapremios.com',
    cert: 'arn:aws:acm:us-east-1:851725546043:certificate/1d585c80-aa05-4a31-8022-6ab7ae6dbedd',
    aliases: ['preview.massapremios.com'],
  },
};

/** Public per-WL / per-stage config (not secrets) */
const WL_stage: Record<WL, Record<EnvStage, WLStageConfig>> = {
  sbt: {
    [EnvStage.HML]: {
      apiUrl: 'https://sbt-hml-api.ilotto.com.br',
      gtmId: 'GTM-52TXX3ZF',
    },
    [EnvStage.PRD]: {
      apiUrl: 'https://api.sbtpremios.com',
      apiInternalAlbHost: 'sbt-prod-api-lb-127487954.us-east-1.elb.amazonaws.com',
      gtmId: 'GTM-NQVQ9FCN',
      turnstileEnforce: true,
    },
  },
  h2b: {
    [EnvStage.HML]: {
      apiUrl: 'https://h2b-hml-api.ilotto.com.br',
      gtmId: 'GTM-52TXX3ZF',
    },
    [EnvStage.PRD]: {
      apiUrl: 'https://api.h2premios.com.br',
      apiInternalAlbHost: 'h2b-prod-api-lb-1497024845.us-east-1.elb.amazonaws.com',
      gtmId: 'GTM-NSPG8F9L',
    },
  },
  orp: {
    [EnvStage.HML]: {
      apiUrl: 'https://orp-hml-api.ilotto.com.br',
    },
    [EnvStage.PRD]: {
      apiUrl: 'https://api.ouropix.com.br',
    },
  },
  wep: {
    [EnvStage.HML]: {
      apiUrl: 'https://wep-hml-api.ilotto.com.br',
    },
    [EnvStage.PRD]: {
      apiUrl: 'https://wep-api.ilotto.com.br',
    },
  },
  demo: {
    [EnvStage.HML]: {
      apiUrl: 'https://demo-hml-api.ilotto.com.br',
    },
    [EnvStage.PRD]: {
      apiUrl: 'https://api-demo.ilotto.com.br',
    },
  },
  msp: {
    [EnvStage.HML]: {
      apiUrl: 'https://msp-hml-api.ilotto.com.br',
      logLevel: 'info',
      forwardLogs: true,
    },
    [EnvStage.PRD]: {
      apiUrl: 'https://api.massapremios.com',
      apiInternalAlbHost: 'msp-v2-prod-api-lb-997145731.us-east-1.elb.amazonaws.com',
      logLevel: 'info',
      forwardLogs: true,
    },
  },
};

export function isProdStage(envStage: EnvStage): boolean {
  return envStage === EnvStage.PRD;
}

export function getFaviconSource(wl: WL, root: boolean = false) {
  const source = root ? `/${WL_static[wl].faviconSource}` : WL_static[wl].faviconSource;
  return source;
}

export function getDomain(wl: WL, envStage: EnvStage) {
  return isProdStage(envStage)
    ? WL_static[wl].domain
    : `${wl}-${envStage}.${NON_PROD_BASE_DOMAIN}`;
}

export function getCert(wl: WL, envStage: EnvStage) {
  return isProdStage(envStage)
    ? (WL_static[wl]?.cert ?? PROD_DEFAULT_CERT)
    : NON_PROD_DEFAULT_CERT;
}

export function getAliases(wl: WL, envStage: EnvStage): string[] | undefined {
  return isProdStage(envStage) ? WL_static[wl]?.aliases : undefined;
}

export function getWlStageConfig(wl: WL, envStage: EnvStage): WLStageConfig {
  return WL_stage[wl][envStage];
}

export function getRumServiceName(wl: WL, envStage: EnvStage): string {
  return `${wl}-${envStage}-front`;
}

export function getApmServiceName(wl: WL, envStage: EnvStage): string {
  return `${envStage}-${wl}-front-lambda`;
}

/** Public site hostname for the WL/stage (used by RUM allowedTracingUrls) */
export function getPublicSiteHost(wl: WL, envStage: EnvStage): string {
  return getDomain(wl, envStage);
}
